import { Report } from './types';
import {
  MAX_AMOUNT,
  MAX_MONTHLY_CAP,
  MAX_THRESHOLD,
  MIN_AMOUNT,
  MIN_MONTHLY_CAP,
  MIN_THRESHOLD,
} from './constants';

import data from '../../config/languages.json';

export const isInProgressReport = (report?: Report | null): boolean =>
  report?.status === 'in_progress';

export const isFinalizationPendingReport = (report?: Report | null): boolean =>
  report?.status === 'finalization_pending';

export const isTransientSessionReport = (report?: Report | null): boolean =>
  isInProgressReport(report) || isFinalizationPendingReport(report);

export const hasRTLChars = (text: React.ReactNode): boolean => {
  const rtlPattern =
    /[\u0590-\u05FF\u0600-\u06FF\u0700-\u074F\u0750-\u077F\u08A0-\u08FF\uFB1D-\uFDFF\uFE70-\uFEFC]/;
  return rtlPattern.test(String(text));
};

export function formatTimestamp(iso?: string) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso ?? '';
  }
}
export function formatDate(iso: string, locale?: string) {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(locale, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

export const limitToRange = (v: number, min: number, max: number): number =>
  Math.max(min, Math.min(max, v));

export const toNum = (s: string): number | undefined =>
  s.trim() === '' ? undefined : Number(s);

export const RANGE = {
  threshold: { min: MIN_THRESHOLD, max: MAX_THRESHOLD },
  amount: { min: MIN_AMOUNT, max: MAX_AMOUNT },
  monthly_cap: { min: MIN_MONTHLY_CAP, max: MAX_MONTHLY_CAP },
} as const;

export const isValidEmail = (email: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

export type LanguageDef = { code: string; name: string; isRTL: boolean };

export const LANGUAGES: LanguageDef[] = data.languages;
export type LanguageCode = (typeof LANGUAGES)[number]['code'];

export const getProfileRequestInfo = (url?: string) => {
  if (!url) {
    return {
      profileId: null,
      isDetailsRequest: false,
      isProfileRequest: false,
    };
  }
  const match = url.match(/\/api\/profiles\/([^/?#]+)(\/.*)?$/);
  if (!match) {
    return {
      profileId: null,
      isDetailsRequest: false,
      isProfileRequest: false,
    };
  }
  const profileId = decodeURIComponent(match[1]);
  if (profileId === 'owned') {
    return {
      profileId: null,
      isDetailsRequest: false,
      isProfileRequest: false,
    };
  }
  const isDetailsRequest = !match[2] || match[2] === '/';
  return { profileId, isDetailsRequest, isProfileRequest: true };
};
